import React, { useEffect, useState, useRef } from 'react';
import './VisaFeeCalculator.css';

// Irish entry-visa fees. Employment-permit fees, VFS fees, insurance and courier
// charges are separate and must be verified with the relevant official provider.
export const VISA_TYPES = {
  student: {
    id: 'student',
    name: 'Student Visa',
    subtitle: 'Stamp 2',
    baseFeeEUR: 60,
    multiEntryFeeEUR: 100,
    description: 'For full-time courses longer than 3 months',
    features: ['Valid for course duration', 'Stamp 2 may allow limited work', 'Multi-entry option available'],
  },
  workPermit: {
    id: 'workPermit',
    name: 'Work Permit',
    subtitle: 'General Employment',
    baseFeeEUR: 60,
    description: 'Long-stay entry visa for a qualifying employment-permit holder; permit costs are separate',
    features: ['Entry visa fee shown only', 'Employment permit is separate', 'Eligibility depends on the permit and role'],
  },
  criticalSkills: {
    id: 'criticalSkills',
    name: 'Critical Skills',
    subtitle: 'Employment Permit',
    baseFeeEUR: 60,
    description: 'Long-stay entry visa for a qualifying Critical Skills permit holder; permit costs are separate',
    features: ['Entry visa fee shown only', 'Employment permit is separate', 'Check the current occupation list and salary rules'],
  },
  tourist: {
    id: 'tourist',
    name: 'Tourist Visa',
    subtitle: 'Short Stay',
    baseFeeEUR: 60,
    description: 'For tourism, visiting family/friends, business meetings',
    features: ['Valid for up to 90 days', 'Single/Multi-entry options', 'Cannot work'],
  },
  dependent: {
    id: 'dependent',
    name: 'Join Family/Dependent',
    subtitle: 'Dependent Visa',
    baseFeeEUR: 60,
    description: 'For joining spouse, parent, or child in Ireland',
    features: ['Valid for same period as primary', 'Can work with permission', 'Study allowed'],
  },
  business: {
    id: 'business',
    name: 'Business/Conference',
    subtitle: 'Short Stay C',
    baseFeeEUR: 60,
    description: 'For business meetings, conferences, training',
    features: ['Valid for up to 90 days', 'No work allowed', 'Multi-entry option'],
  },
  studentExtension: {
    id: 'studentExtension',
    name: 'Student Extension',
    subtitle: 'Stamp 2 Renewal',
    baseFeeEUR: 60,
    description: 'For extending your student visa',
    features: ['Processing: 4-8 weeks', 'Must have valid enrollment', 'Can work during processing'],
  },
  workingHoliday: {
    id: 'workingHoliday',
    name: 'Working Holiday',
    subtitle: 'Visa Waiver',
    baseFeeEUR: 60,
    description: 'Working-holiday arrangements are nationality-specific; verify that you qualify before relying on this estimate',
    features: ['Eligibility varies by nationality', 'Permission conditions vary', 'Separate scheme rules may apply'],
  },
};

export const VFS_SERVICES = {
  standard: {
    id: 'standard',
    name: 'Standard centre service',
    feeINR: 2000,
    processingTime: 'No visa-decision guarantee',
    description: 'Application-centre service; immigration processing is separate',
  },
  premium: {
    id: 'premium',
    name: 'Optional centre add-on',
    feeINR: 4500,
    processingTime: 'No visa-decision guarantee',
    description: 'Optional centre service; does not accelerate the immigration decision',
  },
  premiumPlus: {
    id: 'premiumPlus',
    name: 'Optional lounge add-on',
    feeINR: 8000,
    processingTime: 'No visa-decision guarantee',
    description: 'Optional centre service; does not accelerate the immigration decision',
  },
};

export const EUR_TO_INR = 111.74; // Build-time fallback; runtime fetch refreshes this daily.
export const FX_RATE_ENDPOINT = 'https://api.frankfurter.dev/v2/rate/EUR/INR';

export const parseEurToInrRate = (payload) => {
  const rate = Number(payload?.rate);
  if (!Number.isFinite(rate) || rate <= 0) {
    throw new Error('Invalid EUR to INR exchange rate');
  }
  return rate;
};

const VisaFeeCalculator = () => {
  const [selectedVisa, setSelectedVisa] = useState('student');
  const [multiEntry, setMultiEntry] = useState(false);
  const [dependents, setDependents] = useState(0);
  const [vfsService, setVfsService] = useState('standard');
  const [courierFee, setCourierFee] = useState(false);
  const [insurance, setInsurance] = useState(false);
  const [compareVisa, setCompareVisa] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [expandedVisa, setExpandedVisa] = useState(null);
  const [eurToInr, setEurToInr] = useState(EUR_TO_INR);
  const [exchangeRateState, setExchangeRateState] = useState({ status: 'loading', date: null });
  const printRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    let timedOut = false;
    const timeoutId = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 5000);

    const loadExchangeRate = async () => {
      try {
        const response = await fetch(FX_RATE_ENDPOINT, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Exchange-rate request failed: ${response.status}`);
        const payload = await response.json();
        const rate = parseEurToInrRate(payload);
        if (!cancelled) {
          setEurToInr(rate);
          setExchangeRateState({ status: 'live', date: payload.date || null });
        }
      } catch (error) {
        if (!cancelled && (error.name !== 'AbortError' || timedOut)) {
          setExchangeRateState({ status: 'fallback', date: null });
        }
      } finally {
        window.clearTimeout(timeoutId);
      }
    };

    loadExchangeRate();
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, []);

  // Calculate primary visa fees
  const calculatePrimaryFees = () => {
    const visa = VISA_TYPES[selectedVisa];
    let baseFee = visa.baseFeeEUR;
    
    if (multiEntry && visa.multiEntryFeeEUR > 0) {
      baseFee = visa.multiEntryFeeEUR;
    }
    
    const dependentFee = visa.id === 'dependent' ? dependents * VISA_TYPES.dependent.baseFeeEUR : 0;
    
    return {
      baseFee,
      dependentFee,
      totalEUR: baseFee + dependentFee,
    };
  };

  // Calculate additional fees
  const calculateAdditionalFees = () => {
    const vfs = VFS_SERVICES[vfsService];
    const courier = courierFee ? 500 : 0; // INR
    const ins = insurance ? 75 : 0; // EUR average
    
    return {
      vfsFeeINR: vfs.feeINR,
      vfsFeeEUR: parseFloat((vfs.feeINR / eurToInr).toFixed(2)),
      courierFeeINR: courier,
      courierFeeEUR: parseFloat((courier / eurToInr).toFixed(2)),
      insuranceFeeEUR: ins,
      insuranceFeeINR: parseFloat((ins * eurToInr).toFixed(0)),
    };
  };

  // Calculate comparison fees
  const calculateComparisonFees = () => {
    if (!compareVisa) return null;
    
    const visa = VISA_TYPES[compareVisa];
    const baseFee = visa.baseFeeEUR;
    const dependentFee = visa.id === 'dependent' ? dependents * VISA_TYPES.dependent.baseFeeEUR : 0;
    const vfs = VFS_SERVICES[vfsService];
    const courier = courierFee ? 500 : 0;
    const ins = insurance ? 75 : 0;
    
    return {
      visaName: visa.name,
      baseFee,
      dependentFee,
      vfsFeeINR: vfs.feeINR,
      courierFeeINR: courier,
      insuranceFeeINR: parseFloat((ins * eurToInr).toFixed(0)),
      totalEUR: baseFee + dependentFee + (vfs.feeINR / eurToInr) + (courier / eurToInr) + ins,
      totalINR: (baseFee + dependentFee + (vfs.feeINR / eurToInr) + (courier / eurToInr) + ins) * eurToInr,
    };
  };

  const primaryFees = calculatePrimaryFees();
  const additionalFees = calculateAdditionalFees();
  const comparisonFees = calculateComparisonFees();

  const totalEUR = primaryFees.totalEUR + additionalFees.vfsFeeEUR + additionalFees.courierFeeEUR + additionalFees.insuranceFeeEUR;
  const totalINR = totalEUR * eurToInr;

  const formatCurrency = (amount, currency = 'INR') => {
    if (currency === 'INR') {
      return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    return `€${amount.toFixed(2)}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSave = () => {
    const data = {
      visaType: VISA_TYPES[selectedVisa].name,
      multiEntry,
      dependents,
      vfsService: VFS_SERVICES[vfsService].name,
      courierFee,
      insurance,
      totalEUR,
      totalINR,
      eurToInr,
      exchangeRateDate: exchangeRateState.date,
      exchangeRateSource: exchangeRateState.status === 'live' ? FX_RATE_ENDPOINT : 'build fallback',
      date: new Date().toISOString(),
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'visa-fee-calculation.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="visa-fee-calculator" ref={printRef}>
      <div className="calculator-header">
        <h2>🇮🇪 Ireland Visa Fee Calculator</h2>
        <p className="calculator-subtitle">Estimate entry-visa and optional application costs in EUR and INR</p>
        <p className={`exchange-rate ${exchangeRateState.status === 'fallback' ? 'exchange-rate--fallback' : ''}`}>
          {exchangeRateState.status === 'loading' && 'Loading today\'s ECB reference rate…'}
          {exchangeRateState.status === 'live' && `Today's ECB reference rate: 1 EUR = ₹${eurToInr.toFixed(2)}${exchangeRateState.date ? ` (as of ${exchangeRateState.date})` : ''}.`}
          {exchangeRateState.status === 'fallback' && `Live rate unavailable. Using fallback planning rate: 1 EUR = ₹${eurToInr.toFixed(2)}.`}
          {' '}Provider charges and payment conversion rates may differ.
        </p>
      </div>

      {/* Visa Type Selection */}
      <section className="visa-selection-section">
        <h3>Select Your Visa Type</h3>
        <div className="visa-cards-grid">
          {Object.values(VISA_TYPES).map((visa) => (
            <div
              key={visa.id}
              className={`visa-card ${selectedVisa === visa.id ? 'selected' : ''} ${compareVisa === visa.id ? 'compare-selected' : ''}`}
              onClick={() => {
                setSelectedVisa(visa.id);
                setExpandedVisa(expandedVisa === visa.id ? null : visa.id);
              }}
            >
              <div className="visa-card-header">
                <span className="visa-name">{visa.name}</span>
                <span className="visa-subtitle">{visa.subtitle}</span>
              </div>
              <div className="visa-fee-display">
                {visa.baseFeeEUR === 0 ? (
                  <span className="fee-amount">Free</span>
                ) : (
                  <span className="fee-amount">€{visa.baseFeeEUR}</span>
                )}
                <span className="fee-label">Base Fee</span>
              </div>
              {expandedVisa === visa.id && (
                <div className="visa-details">
                  <p className="visa-description">{visa.description}</p>
                  <ul className="visa-features">
                    {visa.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedVisa === visa.id && <div className="selected-badge">Selected</div>}
            </div>
          ))}
        </div>
      </section>

      {/* Multi-entry option for applicable visas */}
      {(selectedVisa === 'student' || selectedVisa === 'tourist' || selectedVisa === 'business') && (
        <div className="option-toggle">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={multiEntry}
              onChange={(e) => setMultiEntry(e.target.checked)}
            />
            <span className="toggle-text">Multi-entry Visa (+€{VISA_TYPES[selectedVisa].multiEntryFeeEUR - VISA_TYPES[selectedVisa].baseFeeEUR})</span>
            <span className="toggle-info">Recommended for frequent travel</span>
          </label>
        </div>
      )}

      {/* Additional Options */}
      <section className="options-section">
        <h3>Additional Options</h3>
        
        <div className="options-grid">
          {/* Dependents */}
          <div className="option-group">
            <label className="option-label">Number of Dependents</label>
            <div className="dependent-selector">
              {[0, 1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  className={`dependent-btn ${dependents === num ? 'active' : ''}`}
                  onClick={() => setDependents(num)}
                >
                  {num}
                </button>
              ))}
            </div>
            {dependents > 0 && (
              <p className="dependent-info">
                Each dependent: €{VISA_TYPES.dependent.baseFeeEUR} × {dependents} = €{dependents * VISA_TYPES.dependent.baseFeeEUR}
              </p>
            )}
          </div>

          {/* VFS Service */}
          <div className="option-group">
            <label className="option-label">Optional VFS service estimate</label>
            <div className="vfs-options">
              {Object.values(VFS_SERVICES).map((service) => (
                <button
                  key={service.id}
                  className={`vfs-btn ${vfsService === service.id ? 'active' : ''}`}
                  onClick={() => setVfsService(service.id)}
                >
                  <span className="vfs-name">{service.name}</span>
                  <span className="vfs-fee">₹{service.feeINR.toLocaleString()}</span>
                  <span className="vfs-time">{service.processingTime}</span>
                </button>
              ))}
            </div>
          </div>
          <p className="fee-disclaimer">
            VFS, courier and insurance amounts are illustrative planning estimates, not official quotes or faster visa-decision guarantees.
            Confirm current charges with the Irish Immigration Service and VFS India before submitting payment.
          </p>

          {/* Additional Services */}
          <div className="option-group services-group">
            <label className="option-label">Additional Services</label>
            <div className="service-toggles">
              <label className={`service-toggle ${courierFee ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={courierFee}
                  onChange={(e) => setCourierFee(e.target.checked)}
                />
                <span className="service-info">
                  <span className="service-name">Courier Delivery</span>
                  <span className="service-price">₹500</span>
                </span>
              </label>
              
              <label className={`service-toggle ${insurance ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={insurance}
                  onChange={(e) => setInsurance(e.target.checked)}
                />
                <span className="service-info">
                  <span className="service-name">Travel Insurance</span>
                  <span className="service-price">~€75 (₹6,600)</span>
                </span>
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* Fee Summary */}
      <section className="fee-summary-section">
        <div className="summary-header">
          <h3>Fee Breakdown</h3>
          <div className="action-buttons">
            <button className="action-btn" onClick={() => setShowComparison(!showComparison)}>
              {showComparison ? 'Hide' : 'Compare'} Visa Types
            </button>
            <button className="action-btn" onClick={() => setShowTips(!showTips)}>
              💡 Tips
            </button>
            <button className="action-btn" onClick={() => setShowFAQ(!showFAQ)}>
              ❓ FAQ
            </button>
          </div>
        </div>

        {/* Comparison Panel */}
        {showComparison && (
          <div className="comparison-panel">
            <div className="compare-select">
              <label>Compare with:</label>
              <select value={compareVisa || ''} onChange={(e) => setCompareVisa(e.target.value || null)}>
                <option value="">Select a visa type</option>
                {Object.values(VISA_TYPES).map((visa) => (
                  <option key={visa.id} value={visa.id}>
                    {visa.name} (€{visa.baseFeeEUR})
                  </option>
                ))}
              </select>
            </div>
            {comparisonFees && (
              <div className="comparison-results">
                <div className="compare-column">
                  <h4>{VISA_TYPES[selectedVisa].name}</h4>
                  <p className="compare-total">{formatCurrency(totalINR)}</p>
                  <p className="compare-eur">€{totalEUR.toFixed(2)}</p>
                </div>
                <div className="compare-column">
                  <h4>{comparisonFees.visaName}</h4>
                  <p className="compare-total">{formatCurrency(comparisonFees.totalINR)}</p>
                  <p className="compare-eur">€{comparisonFees.totalEUR.toFixed(2)}</p>
                </div>
                <div className="compare-column savings">
                  <h4>Difference</h4>
                  <p className="compare-total">
                    {formatCurrency(Math.abs(totalINR - comparisonFees.totalINR))}
                  </p>
                  <p className="compare-eur">
                    €{Math.abs(totalEUR - comparisonFees.totalEUR).toFixed(2)}
                    {totalINR < comparisonFees.totalINR ? ' cheaper' : ' more expensive'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tips Panel */}
        {showTips && (
          <div className="tips-panel">
            <h4>💡 Tips for Saving on Visa Fees</h4>
            <ul>
              <li><strong>Apply Early:</strong> Standard processing is cheaper than expedited services</li>
              <li><strong>Single Entry:</strong> Only pay for multi-entry if you genuinely need it</li>
              <li><strong>Group Applications:</strong> Some VFS centers offer discounts for families</li>
              <li><strong>Document Checklist:</strong> Incomplete applications incur additional fees</li>
              <li><strong>Insurance Comparison:</strong> Shop around for travel insurance before applying</li>
              <li><strong>Official Channels:</strong> Avoid third-party services that charge extra fees</li>
            </ul>
          </div>
        )}

        {/* FAQ Panel */}
        {showFAQ && (
          <div className="faq-panel">
            <h4>❓ Frequently Asked Questions</h4>
            
            <details className="faq-item">
              <summary>Are visa fees refundable if my application is refused?</summary>
              <p>No, visa application fees are generally non-refundable regardless of the outcome. The application fee covers processing costs.</p>
            </details>
            
            <details className="faq-item">
              <summary>Can I pay fees in INR?</summary>
              <p>Base visa fees are paid in EUR to the Irish government. VFS service fees and courier charges are paid in INR in India.</p>
            </details>
            
            <details className="faq-item">
              <summary>How long are visa fees valid?</summary>
              <p>Fees must be paid when you submit your application. They are not transferable to other applications or individuals.</p>
            </details>
            
            <details className="faq-item">
              <summary>Is health insurance mandatory?</summary>
              <p>While not mandatory for the visa itself, comprehensive travel insurance is highly recommended and required for VFS premium services.</p>
            </details>
            
            <details className="faq-item">
              <summary>Can I get a refund if I withdraw my application?</summary>
              <p>No refunds are provided for withdrawn applications once processing has begun. Administrative rejections may also not be eligible for refunds.</p>
            </details>
            
            <details className="faq-item">
              <summary>What payment methods are accepted?</summary>
              <p>VFS accepts cash, credit/debit cards, and demand drafts. Online payment options may vary by location.</p>
            </details>
          </div>
        )}

        {/* Detailed Breakdown */}
        <div className="breakdown-card">
          <table className="breakdown-table">
            <tbody>
              <tr className="breakdown-row main-fee">
                <td>
                  <span className="fee-item">Application Fee</span>
                  <span className="fee-detail">{VISA_TYPES[selectedVisa].name}</span>
                </td>
                <td className="fee-values">
                  <span className="eur">€{primaryFees.baseFee.toFixed(2)}</span>
                  <span className="inr">{formatCurrency(primaryFees.baseFee * eurToInr)}</span>
                </td>
              </tr>
              
              {primaryFees.dependentFee > 0 && (
                <tr className="breakdown-row">
                  <td>
                    <span className="fee-item">Dependent Fees</span>
                    <span className="fee-detail">{dependents} × €100</span>
                  </td>
                  <td className="fee-values">
                    <span className="eur">€{primaryFees.dependentFee.toFixed(2)}</span>
                    <span className="inr">{formatCurrency(primaryFees.dependentFee * eurToInr)}</span>
                  </td>
                </tr>
              )}
              
              <tr className="breakdown-row">
                <td>
                  <span className="fee-item">VFS Service Fee</span>
                  <span className="fee-detail">{VFS_SERVICES[vfsService].name}</span>
                </td>
                <td className="fee-values">
                  <span className="eur">€{additionalFees.vfsFeeEUR.toFixed(2)}</span>
                  <span className="inr">{formatCurrency(additionalFees.vfsFeeINR)}</span>
                </td>
              </tr>
              
              {courierFee && (
                <tr className="breakdown-row">
                  <td>
                    <span className="fee-item">Courier Fee</span>
                    <span className="fee-detail">Home delivery of passport</span>
                  </td>
                  <td className="fee-values">
                    <span className="eur">€{additionalFees.courierFeeEUR.toFixed(2)}</span>
                    <span className="inr">{formatCurrency(additionalFees.courierFeeINR)}</span>
                  </td>
                </tr>
              )}
              
              {insurance && (
                <tr className="breakdown-row">
                  <td>
                    <span className="fee-item">Travel Insurance</span>
                    <span className="fee-detail">Comprehensive coverage</span>
                  </td>
                  <td className="fee-values">
                    <span className="eur">€{additionalFees.insuranceFeeEUR.toFixed(2)}</span>
                    <span className="inr">{formatCurrency(additionalFees.insuranceFeeINR)}</span>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td>
                  <span className="total-label">Total Estimated Cost</span>
                  <span className="total-note">Subject to change</span>
                </td>
                <td className="total-values">
                  <span className="total-eur">€{totalEUR.toFixed(2)}</span>
                  <span className="total-inr">{formatCurrency(totalINR)}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Important Notes */}
        <div className="notes-section">
          <div className="note includes">
            <h5>✓ Fee Includes:</h5>
            <ul>
              <li>Irish Immigration Service application processing</li>
              <li>VFS handling and document verification</li>
              <li>Standard processing (varies by service level)</li>
            </ul>
          </div>
          
          <div className="note excludes">
            <h5>✗ Fee Excludes:</h5>
            <ul>
              <li>Travel insurance (if not selected)</li>
              <li>Document translation/certification costs</li>
              <li>Premium lounge access (unless selected)</li>
              <li>Return courier (unless selected)</li>
            </ul>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="payment-methods">
          <h5>Accepted Payment Methods:</h5>
          <div className="payment-icons">
            <span className="payment-method">💳 Credit/Debit Cards</span>
            <span className="payment-method">🏦 Bank Transfer</span>
            <span className="payment-method">💵 Cash (at VFS)</span>
            <span className="payment-method">📋 Demand Draft</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="calculator-actions">
          <button className="print-btn" onClick={handlePrint}>
            🖨️ Print Summary
          </button>
          <button className="save-btn" onClick={handleSave}>
            💾 Save Calculation
          </button>
        </div>

        {/* CTA Box */}
        <div className="fee-cta-box">
          <h4>Need Help with Your Visa Application?</h4>
          <p>Get personalized guidance for your Ireland visa process. Our experts can help with documentation, application review, and interview preparation.</p>
          <a href="/contact" className="cta-button">Get Fee Guidance →</a>
        </div>
      </section>
    </div>
  );
};

export default VisaFeeCalculator;
